import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import CategoryCard from '../../components/@servierbar/cards/category-card/category-card.component';
import ItemCard from '../../components/@servierbar/cards/item-card/item-card.component';
import { CardGroup } from 'reactstrap';
import React from 'react';
import arrayMove from 'array-move';
import { basePatchQuery } from '../../utility/sb-api/sb-api.service';
import {
  extractIds,
  getOrderClass,
  mergeOrderConditions,
} from '../../utility/util/util.service';
import InfoCard from '../../components/@servierbar/cards/info-card/info-card.component';

// returns the matching card element, determined by the MenuEntry Type
const getElementByEntryType = (menuEntry, slug, isAdminMode) => {
  switch (menuEntry?.type) {
    case 'CategoryMenuEntry':
      return (
        <CategoryCard
          menuEntry={menuEntry}
          slug={slug}
          isAdminMode={isAdminMode}
        />
      );
    case 'ItemMenuEntry':
      return <ItemCard menuEntry={menuEntry} isAdminMode={isAdminMode} />;
    case 'InfoMenuEntry':
      return <InfoCard menuEntry={menuEntry} isAdminMode={isAdminMode} />;
    default:
      console.error(`"${menuEntry?.type}" is not a valid MenuEntry Type`);
      return null;
  }
};

export const MenuEntry = ({ menuEntry, slug, isAdminMode, filterByPosIntegration }) => {
  const filteredItemData = menuEntry?.itemEntry?.data
    ?.filter(
      ({ orderConditions }) =>
        !orderConditions?.length ||
        orderConditions.some(({ classes }) => classes?.length > 0) ||
        orderConditions.every(({ isActive }) => !isActive),
    )
    .filter(
      (itemData) => !filterByPosIntegration || !!itemData?.posIntegrationRef,
    );

  let modifiedMenuEntry =
    menuEntry?.type === 'ItemMenuEntry' && filteredItemData
      ? {
          ...menuEntry,
          itemEntry: {
            ...menuEntry?.itemEntry,
            data: filteredItemData,
          },
        }
      : menuEntry;
  if (filteredItemData?.length === 0) {
    modifiedMenuEntry = {
      ...modifiedMenuEntry,
      type: undefined, // don't show itemcard if data array is empty
    };
  }
  return getElementByEntryType(modifiedMenuEntry, slug, isAdminMode);
};

const SortableMenuEntry = SortableElement(({ menuEntry, slug, isAdminMode }) =>
  getElementByEntryType(menuEntry, slug, isAdminMode),
);

// returns sortable elements when isAdminMode=true
const getGroupByAdminMode = (
  menuEntries,
  slug,
  isAdminMode,
  filterByPosIntegration,
) => {
  return (
    <CardGroup className="row match-height">
      {menuEntries?.map((menuEntry, index) => {
        const key = menuEntry?.id;
        const props = {
          index: index,
          menuEntry: menuEntry,
          slug: slug,
          isAdminMode: isAdminMode,
          filterByPosIntegration: filterByPosIntegration,
        };
        return isAdminMode ? (
          <SortableMenuEntry key={key} {...props} />
        ) : (
          <MenuEntry key={key} {...props} />
        );
      })}
    </CardGroup>
  );
};

export const MenuEntryList = ({ menuEntries, slug, filterByPosIntegration }) =>
  getGroupByAdminMode(menuEntries, slug, false, filterByPosIntegration);

export const SortableMenuEntryList = SortableContainer(
  ({ menuEntries, slug }) => getGroupByAdminMode(menuEntries, slug, true),
);

export const onSortEnd = (
  menuEntries,
  setMenuEntries,
  sortEnd,
  categoryId,
  fetchMenuEntries,
) => {
  const newMenuEntriesOrder = arrayMove(
    menuEntries,
    sortEnd.oldIndex,
    sortEnd.newIndex,
  );
  setMenuEntries(newMenuEntriesOrder);
  // Only send entries whose menuEntry.category matches the current category.
  // Secondary-matched entries (matched via item.categories) have a different
  // menuEntry.category and would be rejected by the backend.
  const getCatId = (cat) => {
    if (!cat) return null;
    return typeof cat === 'string' ? cat : cat.id;
  };
  const rankableEntries = newMenuEntriesOrder.filter(
    (entry) => getCatId(entry.category) === (categoryId || null),
  );
  basePatchQuery(
    `menu-entries/${categoryId || 'root'}/manual-ranking`,
    { manualRankings: extractIds(rankableEntries) },
    null,
    'changesSaved',
    'changesSavedError',
  ).then(() => fetchMenuEntries());
};

export const filterMenuEntries = (
  currentMenuEntries,
  foodIngredientsFilter,
  foodAllergensFilter,
  foodPreferencesFilter,
) => {
  const forbiddenIngredientIds = extractIds(foodIngredientsFilter);
  const forbiddenAllergenIds = extractIds(foodAllergensFilter);
  const allowedPreferenceIds = extractIds(foodPreferencesFilter);

  const filteredItemMenuEntries = currentMenuEntries
    .filter(({ type, itemEntry }) => type === 'ItemMenuEntry' && itemEntry?.data)
    .map((menuEntry) => ({
      ...menuEntry,
      itemEntry: {
        ...menuEntry.itemEntry,
        data: menuEntry.itemEntry.data.filter(
          (itemData) =>
            // OR filter for ingredients
            itemData.ingredients.every(
              ({ id }) => !forbiddenIngredientIds.includes(id),
            ) &&
            // OR filter for allergens
            itemData.allergens.every(
              ({ id }) => !forbiddenAllergenIds.includes(id),
            ) &&
            // AND filter for preferences
            allowedPreferenceIds.every((id) =>
              extractIds(itemData.preferences).includes(id),
            ),
        ),
      },
    }))
    .filter(({ itemEntry }) => itemEntry?.data?.length > 0); // remove ItemMenuEntries with empty data

  // merge untouched CategoryMenuEntries with the filtered ItemMenuEntries
  return [
    ...filteredItemMenuEntries,
    ...currentMenuEntries.filter(
      ({ type }) => type === 'CategoryMenuEntry' || type === 'InfoMenuEntry',
    ),
  ].sort((a, b) => a.manualRanking - b.manualRanking);
};
